<template lang="pug">
section.business-cards-per-stock
    el-form-item(:label="$t('Stocks with a business card')")
        el-select.is-full-width(
            :value="selectedStockIds"
            multiple
            filterable
            @change="onStockSelectionChange"
        )
            el-option(
                v-for="stock in shopStocks"
                :key="stock.id"
                :label="formatStockLabel(stock)"
                :value="parseInt(stock.id, 10)"
            )

    .empty-hint(v-if="businessCards.length === 0")
        | {{ $t('Pick at least one stock to add a business card.') }}

    el-collapse(v-else v-model="openPanels" accordion)
        el-collapse-item(
            v-for="(card, index) in businessCards"
            :key="parseInt(card.stock.id, 10)"
            :name="parseInt(card.stock.id, 10)"
            :title="formatStockLabel(card.stock)"
        )
            BusinessCardForm(
                :value="card"
                :cms="cmsForm"
                :propPath="`businessCards.${index}`"
                :category-options="businessCardCategoryOptions"
                :tag-options="businessCardTagOptions"
                @remove-address="removeOverrideAddress(card.stock.id)"
            )
                template(#address-fallback)
                    .fallback-panel
                        .flex-row
                            .info.grow
                                .fallback-label {{ $t('Stock address') }}
                                .fallback-name(v-if="stockAddressName(card.stock)") {{ stockAddressName(card.stock) }}
                                .fallback-address(v-if="card.stock && card.stock.address") {{ formatStockAddress(card.stock.address) }}
                                .no-address(v-else) {{ $t('Stock has no address') }}
                            .actions
                                el-button(
                                    plain
                                    circle
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-pencil-alt fas"
                                    @click="initOverrideAddress(card.stock.id)"
                                )
</template>

<i18n>
{
    "de": {
        "Stocks with a business card": "Lager mit Visitenkarte",
        "Pick at least one stock to add a business card.": "Wähle mindestens ein Lager, um eine Visitenkarte hinzuzufügen.",
        "Stock address": "Lageradresse",
        "Stock has no address": "Lager hat keine Adresse"
    }
}
</i18n>

<script>
import _ from 'lodash'

import { useSettingsStore } from '@/stores'

import BusinessCardForm from '@/components/businessCard/BusinessCardForm'

export default {
    name: 'BusinessCardsPerStock',
    components: {
        BusinessCardForm,
    },
    props: {
        shopStocks: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            openPanels: null,
        }
    },
    watch: {
        // Form.vue's submit handler sets `invalidBusinessCardStockIds` when a
        // submit fails because of errors inside per-stock cards. Pop the
        // first invalid panel open IF the user isn't already on an invalid
        // one — staying on a panel they were just looking at is less jarring
        // than ripping it closed.
        invalidBusinessCardStockIds: {
            immediate: true,
            handler(stockIds) {
                if (!stockIds || !stockIds.length) return
                const numericIds = stockIds.map((id) => parseInt(id, 10))
                const current = this.openPanels !== null ? parseInt(this.openPanels, 10) : null
                if (current === null || numericIds.indexOf(current) < 0) {
                    this.openPanels = numericIds[0]
                }
                this.$nextTick(() => {
                    useSettingsStore().clearInvalidBusinessCardStockIds()
                })
            },
        },
    },
    computed: {
        cmsForm() {
            return useSettingsStore().form
        },
        businessCards() {
            return useSettingsStore().form.businessCards || []
        },
        businessCardCategoryOptions() {
            const options = useSettingsStore().options.businessCardCategory
            return (options && options.items) || []
        },
        businessCardTagOptions() {
            const options = useSettingsStore().options.businessCardTag
            return (options && options.items) || []
        },
        selectedStockIds() {
            return this.businessCards.map((card) => parseInt(_.get(card, 'stock.id'), 10))
        },
        invalidBusinessCardStockIds() {
            return useSettingsStore().invalidBusinessCardStockIds
        },
    },
    methods: {
        formatStockLabel(stock) {
            if (!stock) return ''
            return stock.name || stock.key || ('#' + stock.id)
        },
        stockAddressName(stock) {
            const address = _.get(stock, 'address')
            if (!address) return ''
            const parts = [address.first_name, address.last_name].filter(Boolean).join(' ')
            return parts || address.company || ''
        },
        formatStockAddress(address) {
            if (!address) return ''
            return [address.street, address.zipcode, address.city, address.country]
                .filter(Boolean)
                .join(', ')
        },
        onStockSelectionChange(newStockIds) {
            const store = useSettingsStore()
            const previous = this.selectedStockIds
            const added = newStockIds.filter((id) => !previous.includes(parseInt(id, 10)))
            const removed = previous.filter((id) => !newStockIds.includes(parseInt(id, 10)))

            added.forEach((stockId) => {
                const stock = this.shopStocks.find((s) => parseInt(s.id, 10) === parseInt(stockId, 10))
                if (stock) {
                    store.addBusinessCardForStock(stock)
                }
            })

            removed.forEach((stockId) => {
                store.removeBusinessCardForStock(stockId)
            })
        },
        initOverrideAddress(stockId) {
            useSettingsStore().initBusinessCardsAddress(stockId)
        },
        removeOverrideAddress(stockId) {
            useSettingsStore().removeBusinessCardsAddress(stockId)
        },
    },
}
</script>

<style lang="sass" scoped>
.business-cards-per-stock
    .empty-hint
        padding: 16px 0
        color: #909399
        font-style: italic

.fallback-panel
    background: #f5f7fa
    border-radius: 4px
    padding: 12px 16px
    min-height: 36px
    line-height: 1.3
    .flex-row
        display: flex
        gap: 20px
        align-items: center
        .grow
            flex: 1
    .info
        .fallback-label
            font-size: 0.75rem
            color: #909399
            letter-spacing: 0.5px
            margin-bottom: 4px
        .fallback-name
            font-weight: 500
            color: #303133
        .fallback-address
            color: #606266
            font-size: 0.9rem
        .no-address
            color: #909399
            font-style: italic
</style>
